import type { CaptureKind } from './types.ts';

export interface AtlasDebugSession {
  scenarioId: string;
  appId: string;
  routeId: string;
  stateId: string;
  captureKind: CaptureKind;
  pathname: string;
  search: string;
}

export interface AtlasDebugBootstrap {
  cookies?: Record<string, string>;
  localStorage?: Record<string, string>;
  sessionStorage?: Record<string, string>;
}

export interface AtlasDebugMockResponse {
  status?: number;
  body?: unknown;
  headers?: Record<string, string>;
}

export interface AtlasDebugRequest {
  url: string;
  method: string;
  body: unknown;
  pathname: string;
  search: string;
}

export interface AtlasDebugAppAdapter {
  appId: string;
  getBootstrap?(session: AtlasDebugSession): AtlasDebugBootstrap | undefined;
  getMockResponse?(
    request: AtlasDebugRequest,
    session: AtlasDebugSession,
  ): AtlasDebugMockResponse | typeof ATLAS_PENDING | undefined;
}

export interface AtlasDebugStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface AtlasDebugDocumentLike {
  cookie: string;
}

export interface AtlasDebugWindowLike {
  document: AtlasDebugDocumentLike;
  localStorage?: AtlasDebugStorageLike;
  sessionStorage?: AtlasDebugStorageLike;
}

const ATLAS_SCENARIO_QUERY_PARAM = 'atlasScenario';
export const ATLAS_PENDING = Symbol('ATLAS_PENDING');

export function parseAtlasDebugSessionFromUrl(
  urlValue: string,
  expectedAppId: string,
): AtlasDebugSession | null {
  const url = new URL(urlValue);
  const scenarioId = url.searchParams.get(ATLAS_SCENARIO_QUERY_PARAM);

  if (!scenarioId) {
    return null;
  }

  const parts = scenarioId.split('__');
  if (parts.length !== 4) {
    return null;
  }

  const appId = parts[0];
  const routeId = parts[1];
  const stateId = parts[2];
  const captureKind = parts[3];
  if (!appId || !routeId || !stateId || !captureKind) {
    return null;
  }
  if (appId !== expectedAppId) {
    return null;
  }

  if (!isCaptureKind(captureKind)) {
    return null;
  }

  return {
    scenarioId,
    appId,
    routeId,
    stateId,
    captureKind,
    pathname: url.pathname,
    search: url.search,
  };
}

export function buildAtlasLocalUrl(
  localBaseUrl: string | undefined,
  routePath: string,
  scenarioId: string,
): string | undefined {
  if (!localBaseUrl) {
    return undefined;
  }

  const url = new URL(routePath, ensureTrailingSlash(localBaseUrl));
  url.searchParams.set(ATLAS_SCENARIO_QUERY_PARAM, scenarioId);
  return url.toString();
}

export function applyAtlasDebugBootstrap(
  target: AtlasDebugWindowLike,
  bootstrap: AtlasDebugBootstrap | undefined,
): void {
  if (!bootstrap) {
    return;
  }

  for (const [key, value] of Object.entries(bootstrap.localStorage ?? {})) {
    target.localStorage?.setItem(key, value);
  }

  for (const [key, value] of Object.entries(bootstrap.sessionStorage ?? {})) {
    target.sessionStorage?.setItem(key, value);
  }

  for (const [key, value] of Object.entries(bootstrap.cookies ?? {})) {
    target.document.cookie = `${key}=${encodeURIComponent(value)}; path=/`;
  }
}

function ensureTrailingSlash(url: string): string {
  return url.endsWith('/') ? url : `${url}/`;
}

function isCaptureKind(value: string): value is CaptureKind {
  return value === 'top' || value === 'full' || value === 'segment' || value === 'interaction';
}
